import React from 'react';
import Header from '../common/Header';
import Hero from '../common/Hero';
import Services from '../common/Services';
import About from '../common/About';
import Pricing from '../common/Pricing';
import Domains from '../common/Domains';
import CallToAction from '../common/CallToAction';
import Team from '../common/Team';
import Testimonials from '../common/Testimonials';
import Contact from '../common/Contact';
import Footer from '../common/Footer';
import Navigation from '../layout/Navigation';
import ScrollManager from '../layout/scrollManager';
import SideContactBar from '../layout/SideContactBar';
import SEOHead from '../layout/SEOHead';
import { SITE_URL, BUSINESS } from '../../config/seo';

const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'DryCleaningOrLaundry',
    '@id': `${SITE_URL}/#business`,
    name: BUSINESS.name,
    url: BUSINESS.url,
    logo: {
        '@type': 'ImageObject',
        url: BUSINESS.logo,
    },
    image: BUSINESS.image,
    description: BUSINESS.description,
    telephone: BUSINESS.telephone,
    email: BUSINESS.email,
    address: {
        '@type': 'PostalAddress',
        streetAddress: BUSINESS.address.streetAddress,
        addressLocality: BUSINESS.address.addressLocality,
        addressRegion: BUSINESS.address.addressRegion,
        postalCode: BUSINESS.address.postalCode,
        addressCountry: BUSINESS.address.addressCountry,
    },
    geo: {
        '@type': 'GeoCoordinates',
        latitude: BUSINESS.geo.latitude,
        longitude: BUSINESS.geo.longitude,
    },
    openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday'], opens: '09:00', closes: '19:30' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Tuesday'], opens: '09:00', closes: '19:30' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Thursday'], opens: '09:00', closes: '19:30' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Friday'], opens: '09:00', closes: '19:30' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '09:00', closes: '19:30' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Sunday'], opens: '09:00', closes: '19:30' },
    ],
    priceRange: BUSINESS.priceRange,
    hasMap: BUSINESS.hasMap,
    sameAs: BUSINESS.sameAs,
    aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: BUSINESS.aggregateRating.ratingValue,
        reviewCount: BUSINESS.aggregateRating.reviewCount,
        bestRating: BUSINESS.aggregateRating.bestRating,
        worstRating: BUSINESS.aggregateRating.worstRating,
    },
    areaServed: {
        '@type': 'GeoCircle',
        geoMidpoint: {
            '@type': 'GeoCoordinates',
            latitude: BUSINESS.geo.latitude,
            longitude: BUSINESS.geo.longitude,
        },
        geoRadius: '10000',
    },
    makesOffer: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Dry Cleaning', description: 'Expert dry cleaning for delicate garments using eco-friendly solvents.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Steam Ironing', description: 'Professional steam ironing for a crisp, wrinkle-free finish.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Wash & Iron', description: 'Complete washing and professional ironing for everyday clothing.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Wash & Fold', description: 'Full wash, dry, and fold service with temperature-controlled washing.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Carpet Cleaning', description: 'Deep extraction cleaning for carpets and rugs.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Curtain Cleaning', description: 'Specialized cleaning for curtains and drapes of all fabric types.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Shoe Cleaning', description: 'Expert cleaning for all footwear types including sneakers and leather shoes.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Leather Cleaning', description: 'Professional cleaning and conditioning for leather garments and accessories.' } },
    ],
    currenciesAccepted: 'INR',
    paymentAccepted: 'Cash, UPI, Online Payment',
};

const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: BUSINESS.name,
    url: SITE_URL,
    description: BUSINESS.description,
    publisher: { '@id': `${SITE_URL}/#business` },
    potentialAction: {
        '@type': 'SearchAction',
        target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/#pricing?q={search_term_string}` },
        'query-input': 'required name=search_term_string',
    },
};

const homeFAQSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
        {
            '@type': 'Question',
            name: 'Does Urban Shine Laundry offer home pickup and delivery?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes! Free home pickup and delivery is available for orders above ₹250. Our team collects your items from your doorstep and returns them clean and neatly packed.' },
        },
        {
            '@type': 'Question',
            name: 'What are Urban Shine Laundry\'s operating hours?',
            acceptedAnswer: { '@type': 'Answer', text: 'We are open from 9:00 AM to 7:30 PM, Thursday to Tuesday (closed on Wednesdays).' },
        },
        {
            '@type': 'Question',
            name: 'Where is Urban Shine Laundry located?',
            acceptedAnswer: { '@type': 'Answer', text: 'Our store is at DF 05, Street Number 316, DF Block, New Town, Kolkata 700156.' },
        },
        {
            '@type': 'Question',
            name: 'What services does Urban Shine Laundry offer?',
            acceptedAnswer: { '@type': 'Answer', text: 'We offer Dry Cleaning, Steam Ironing, Wash & Iron, Wash & Fold, Carpet Cleaning, Curtain Cleaning, Shoe Cleaning, and Leather Cleaning. All services include free home pickup and delivery.' },
        },
        {
            '@type': 'Question',
            name: 'What is the minimum order value for free pickup?',
            acceptedAnswer: { '@type': 'Answer', text: 'The minimum order value for free pickup and delivery is ₹250.' },
        },
        {
            '@type': 'Question',
            name: 'What payment methods does Urban Shine Laundry accept?',
            acceptedAnswer: { '@type': 'Answer', text: 'We accept Cash, UPI (GPay, PhonePe, Paytm), and other digital payment methods. Payment is collected at the time of delivery.' },
        },
    ],
};

const Home = () => {
  return (
    <ScrollManager>
      <SEOHead
        title="Urban Shine Laundry | Professional Dry Cleaning & Laundry in New Town, Kolkata"
        description="Urban Shine Laundry offers expert dry cleaning, steam ironing, wash & fold, carpet & shoe cleaning with FREE home pickup & delivery in New Town, Kolkata. 4.9★ rated. Book now!"
        keywords="laundry service New Town Kolkata, dry cleaning New Town, steam ironing Kolkata, wash and fold Kolkata, laundry pickup delivery Kolkata, carpet cleaning New Town, shoe cleaning Kolkata, leather cleaning Kolkata, professional laundry Kolkata, Urban Shine Laundry, Action Area laundry, DF Block New Town laundry"
        canonicalPath="/"
        structuredData={[localBusinessSchema, websiteSchema, homeFAQSchema]}
      />
      <div className="flex flex-col min-h-screen font-sans">
        <Header />
        {/* <Navigation /> */}
        <SideContactBar />
        <main>
          <section id="home" className="scroll-snap-section">
            <Hero />
          </section>
          <section id="services" className="scroll-snap-section">
            <Services />
          </section>
          {/* <section id="about" className="scroll-snap-section">
            <About />
          </section> */}
          <section id="pricing" className="scroll-snap-section">
            <Pricing />
          </section>
          {/* <section id="domains" className="scroll-snap-section">
            <Domains />
          </section> */}
          {/* <section id="team" className="scroll-snap-section">
            <CallToAction />
            <Team />
          </section> */}
          <section id="testimonials" className="scroll-snap-section">
            <Testimonials />
          </section>
          <section id="contact" className="scroll-snap-section">
            <Contact />
          </section>
        </main>
        <Footer />
      </div>
    </ScrollManager>
  );
};

export default Home;